import { z } from 'zod';

import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from './constants.js';

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(254);

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Keep it under ${PASSWORD_MAX_LENGTH} characters`)
  .refine((v) => /[a-z]/.test(v), 'Must include a lowercase letter')
  .refine((v) => /[A-Z]/.test(v), 'Must include an uppercase letter')
  .refine((v) => /\d/.test(v), 'Must include a digit');

export const totpCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');

export const loginInput = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
  totpCode: totpCodeSchema.optional(),
  rememberMe: z.boolean().optional().default(false),
});
export type LoginInput = z.infer<typeof loginInput>;

/** Super-admin login requires TOTP — the schema enforces it. */
export const superAdminLoginInput = loginInput.extend({
  totpCode: totpCodeSchema,
});
export type SuperAdminLoginInput = z.infer<typeof superAdminLoginInput>;

export const refreshInput = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshInput = z.infer<typeof refreshInput>;

export const forgotPasswordInput = z.object({
  email: emailSchema,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInput>;

export const resetPasswordInput = z.object({
  token: z.string().min(1),
  newPassword: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

export const magicLinkRequestInput = z.object({
  email: emailSchema,
});
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestInput>;

export const magicLinkConsumeInput = z.object({
  token: z.string().min(1),
});
export type MagicLinkConsumeInput = z.infer<typeof magicLinkConsumeInput>;

export const acceptInvitationInput = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationInput>;

export const inspectInvitationInput = z.object({
  token: z.string().min(1),
});
export type InspectInvitationInput = z.infer<typeof inspectInvitationInput>;

export const totpSetupVerifyInput = z.object({
  code: totpCodeSchema,
});
export type TotpSetupVerifyInput = z.infer<typeof totpSetupVerifyInput>;

export const totpDisableInput = z.object({
  password: z.string().min(1),
  code: totpCodeSchema,
});
export type TotpDisableInput = z.infer<typeof totpDisableInput>;

/** Tokens returned by the API after login or refresh. */
export const authTokens = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.string().datetime(),
});
export type AuthTokens = z.infer<typeof authTokens>;

/** Public shape of the current user returned in `/me` and embedded in login responses. */
export const currentUser = z.object({
  id: z.string().uuid(),
  email: emailSchema,
  name: z.string(),
  role: z.string(),
  isSuperAdmin: z.boolean(),
  totpEnabled: z.boolean(),
  orgId: z.string().uuid().nullable(),
  /** Phase 9 — phone number used for SMS notifications, if any. */
  phone: z.string().nullable().optional(),
  phoneVerifiedAt: z.string().nullable().optional(),
  /** Wall tablet: WO-only shell; cannot complete/cancel work orders. */
  tabletMode: z.boolean().optional().default(false),
});
export type CurrentUser = z.infer<typeof currentUser>;
